import {
  Theme, WithStyles,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import {
  Visibility, VisibilityOff,
} from '@material-ui/icons';
import classNames from 'classnames';
import {
  Form, Formik,
} from 'formik';
import {
  inject, observer,
} from 'mobx-react';
import React, {
  Component,
} from 'react';
import {
  Redirect,
} from 'react-router-dom';
import * as Yup from 'yup';
import Directives from '../../components/Directives/Directives';
import Spinner from '../../components/Spinner';
import {
  translate,
} from '../../helperFunctions/translation';
import {
  IAuthStore,
} from '../../mobx-store/storeInterfaces/authInterface';
import classes from './login.module.scss';

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .required('Required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Too Long!')
    .required('Required'),
});

const styles = (theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    flexBasis: 200,
  },
  button: {
    margin: 4,
  },
});

interface IProps extends WithStyles<typeof styles> {
  authStore: IAuthStore;
  history: any;
}

interface State {
  email: string;
  password: string;
  errorInLogging: string;
  showPassword: boolean;
}

@inject('authStore')
@observer
class Login extends Component<IProps, State> {
  emailSuffix = '@wadidegla.com';

  state = {
    email: '',
    password: '',
    errorInLogging: '',
    showPassword: false,
  };

  handleSubmit = (values: any) => {
    const email = `${values.email}${this.emailSuffix}`;
    this.props.authStore.login(email, values.password)
      .then(() => {
        if (!this.props.authStore.isAuthenticated) {
          this.setState({
            errorInLogging: translate('loginScreen.loginError', {
            }),
          });
        }
      })
      .catch(err => console.log('wwwww', err))
  };

  handleForgotPassword = () => {
    this.props.history.push('/forgotPassword');
  };

  handleChange = (prop: 'email' | 'password') => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.setState({
      [prop]: event.target.value,
    } as Pick<State, typeof prop>);
  };

  handleClickShowPassword = () => {
    this.setState(({
      showPassword: !this.state.showPassword,
    }));
  };

  render() {
    const uiClasses = this.props.classes;
    const {
      authStore,
    } = this.props;
    let spinner: any;

    if (this.props.authStore.isAuthenticated) {
      return <Redirect to="/" />;
    }

    if (authStore.logging) {
      spinner = <Spinner> </Spinner>;
    }

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={{
            email: this.state.email,
            password: this.state.password,
          }}
          validationSchema={LoginSchema}
          onSubmit={(values) => {
            // same shape as initial values
            console.log(values);
            this.handleSubmit(values);
          }}
        >
          {({
            errors, touched,
          }) => (
            <div className={classNames(classes.loginCard)}>
              <Typography variant="h5" component="h2"> Login </Typography>
              <Form className={classes.loginForm}>
                <FormControl className={classNames(uiClasses.margin, classes.field)}>
                  <TextField
                    name="email"
                    id="outlined-simple-start-adornment"
                    variant="outlined"
                    onChange={this.handleChange('email')}
                    label={translate('loginScreen.email', {
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{this.emailSuffix}</InputAdornment>,
                    }}
                  />
                  <Directives dIf={errors.email && touched.email}>
                    <FormHelperText className={classes.error}>{errors.email}</FormHelperText>
                  </Directives>
                </FormControl>
                <FormControl className={classNames(uiClasses.margin, classes.field)}>
                  <TextField
                    name="password"
                    id="filled-adornment-password"
                    variant="outlined"
                    type={this.state.showPassword ? 'text' : 'password'}
                    label="Password"
                    value={this.state.password}
                    onChange={this.handleChange('password')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                  aria-label="Toggle password visibility"
                  onClick={this.handleClickShowPassword}
                >
                  <Directives dIf={this.state.showPassword}>
                              <VisibilityOff />
                            </Directives>
                  <Directives dIf={!this.state.showPassword}>
                              <Visibility />
                            </Directives>
                </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Directives dIf={errors.password && touched.password}>
                    <FormHelperText className={classNames(classes.error)}>
                      {errors.password}
                    </FormHelperText>
                  </Directives>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  className={classNames(uiClasses.button)}
                >
                  {translate('loginScreen.loginButton', {
                  })}
                </Button>
                <Directives dIf={this.state.errorInLogging}>
                  <FormHelperText className={classNames(classes.error)}>
                    {this.state.errorInLogging}
                  </FormHelperText>
                </Directives>
              </Form>
              <Button
                variant="contained"
                color="secondary"
                type="submit"
                className={classNames(uiClasses.button)}
                onClick={() => this.handleForgotPassword()}
              >
                {translate('loginScreen.forgotPasswordButton', {
                })}
              </Button>
              {spinner}
            </div>
          )}
        </Formik>
      </div>
    );
  }
}

export default withStyles(styles)(Login);
